import { Backdrop, CircularProgress } from "@mui/material";
import {
  Button,
  Col,
  Row,
  Table,
  Typography,
  Modal,
  TableProps,
  Form,
  Select,
  Tag,
  Tooltip,
  List,
  Skeleton,
} from "antd";
import { useEffect, useState } from "react";
import AdminLayout from "../../../../component/AdminLayout";
import SearchBar from "../../../../component/Search";
import { customSortDirections } from "../../../../Utils/utils";
import { EditOutlined } from "@ant-design/icons";
import { SorterResult } from "antd/es/table/interface";
import { enqueueSnackbar } from "notistack";
import {
  GetEmailLicensingGetEmailLicense,
  UpdateEmployeeAsResigned,
  axiosErrorMessages,
} from "../../../../API/api";
import AddEditEmailList from "../components/AddEditEmailList";
import { useAppRole } from "../../../../API/RoleContext";
import FilterEmailList from "../components/FilterEmailList";
import axios, { AxiosError } from "axios";

interface EmailItem {
  id: number;
  employeeName: string;
  domainName: string;
  emailId: string;
  group: [];
  routing: boolean;
  specialEmailId: string;
  key: number;
}

const EmailList = () => {
  const userAppRole = useAppRole();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filter, setFilter] = useState(false);
  const [isFilter, setIsFilter] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSpecialEmailIdModalOpen, setSpecialEmailIdModal] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState<EmailItem[]>([]);
  const [emailList, setEmailList] = useState([]);
  const [groups, setGroups] = useState([]);
  const [specialEmailIds, setSpecialEmailIds] = useState([]);
  const [isAddNewGroup, setIsAddNewGroup] = useState<boolean>(false);
  const [editData, setEditData] = useState({});
  const [isAdd, setIsAdd] = useState<boolean>(true);
  const [sortedInfo, setSortedInfo] = useState<SorterResult<EmailItem>>({});
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [pagesize, setPageSize] = useState<number>(50);

  const [postData, setPostData] = useState({
    groupId: [],
    domains: [],
    employeeStatus: 1,
    routingStatus: 0,
    haveSpecialEmail: 0,
    specialEmailIds: []
  });

  useEffect(() => {
    fetchEmailGroups();
  }, []);

  useEffect(() => {
    fetchEmailGroups();
  }, [postData]);

  const fetchEmailGroups = async () => {
    try {
      setIsLoading(true);
      GetEmailLicensingGetEmailLicense(postData).then((response: any) => {
        const newData = response.data.map(
          (emailLicensing: any, index: number) => ({
            ...emailLicensing,
            key: emailLicensing.emailLicensingId,
            specialEmail: emailLicensing.specialEmails.map((item: any) => {
              return item.specialEmail;
            }),
          })
        );
        setEmailList(newData);
        setIsLoading(false);
      });
    } catch (error: any) {
      let errorMsg;
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        errorMsg = axiosErrorMessages(axiosError);
      } else {
        errorMsg = error.message;
      }
      enqueueSnackbar(errorMsg, {
        variant: "error",
        anchorOrigin: {
          vertical: "top",
          horizontal: "right",
        },
      });

      setIsLoading(false);
    }
  };

  const columns = [
    {
      title: "No",
      dataIndex: "id",
      key: "id",
      width: "5%",

      render: (text: any, record: any, index: number) =>
        (currentPage - 1) * pagesize + index + 1,
    },
    {
      title: "Employee Name",
      dataIndex: "employeeName",
      key: "employeeName",
      width: "20%",
      sorter: (a: any, b: any) => a.employeeName.localeCompare(b.employeeName),
      sortOrder:
        sortedInfo.columnKey === "employeeName" ? sortedInfo.order : null,
      render: (text: string) => (
        <Tooltip placement="topLeft" title={text}>
          <div className="truncate-text">{text}</div>
        </Tooltip>
      ),
    },
    {
      title: "Domain",
      dataIndex: "domainName",
      key: "domainName",
      width: "10%",

      sorter: (a: any, b: any) => a.domainName.localeCompare(b.domainName),
      sortOrder:
        sortedInfo.columnKey === "domainName" ? sortedInfo.order : null,
      render: (text: string) => (
        <Tooltip placement="topLeft" title={text}>
          <div className="truncate-text">{text}</div>
        </Tooltip>
      ),
    },
    {
      title: "Email ID",
      dataIndex: "emailId",
      key: "emailId",
      width: "20%",
      sorter: (a: any, b: any) => a.emailId.localeCompare(b.emailId),
      sortOrder: sortedInfo.columnKey === "emailId" ? sortedInfo.order : null,
      render: (text: string) => (
        <Tooltip placement="topLeft" title={text}>
          <div className="truncate-text">{text}</div>
        </Tooltip>
      ),
    },

    {
      title: "Group",
      dataIndex: "group",
      key: "group",
      width: "10%",

      render: (text: any) => {
        return (
          <div>
            <Button type="link" onClick={() => handleGroupName(text)}>
              View
            </Button>
          </div>
        );
      },
    },
    {
      title: "Routing",
      dataIndex: "routing",
      key: "routing",
      width: "10%",

      render: (text: boolean) => {
        const className = text ? "yesClass" : "noClass"; // Define CSS classes

        return (
          <div className={className}>
            <Tag color={text ? "green" : "red"}>{text ? "Yes" : "No"}</Tag>{" "}
          </div>
        );
      },
    },
    {
      title: "Special Email",
      dataIndex: "specialEmail",
      key: "specialEmail",
      width: "10%",
      sorter: (a: any, b: any) => a.specialEmail.length - b.specialEmail.length,
      sortOrder:
        sortedInfo.columnKey === "specialEmail" ? sortedInfo.order : null,

      render: (text: string) => (
        <div>
          <Button
            type="link"
            disabled={text.length === 0}
            onClick={() => showSpecialEmailIds(text)}
          >
            {text.length}
          </Button>
        </div>
      ),
    },
    ...(userAppRole
      ? [
          {
            title: "Action",
            dataIndex: "action",
            width: "10%",

            render: (_: any, record: any) => {
              return (
                <>
                  {" "}
                  <Typography.Link
                    onClick={() => edit(record)}
                    disabled={!record.isActive} // Disable the button if isActive is false
                  >
                    <EditOutlined />
                  </Typography.Link>
                </>
              );
            },
          },
        ]
      : []),
  ];

  const handleSearch = (value: string) => {
    setCurrentPage(1);
    setSearchText(value);
    filterData(value);
  };

  const filterData = (searchText: string) => {
    if (!searchText) {
      setFilteredData([]);
    } else {
      const filtered = emailList.filter(
        (item: any) =>
          item.emailId.toLowerCase().includes(searchText.toLowerCase()) ||
          item.employeeName.toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredData(filtered);
    }
  };

  const handleGroupName = (text: any) => {
    setGroups(text);
    setIsModalOpen(true);
  };
  const showSpecialEmailIds = (text: any) => {
    setSpecialEmailIds(text);
    setSpecialEmailIdModal(true);
  };
  const handleAddNewEmailList = () => {
    setIsAdd(true);
    setIsAddNewGroup(true);
  };

  const handleFilter = () => {
    setIsFilter(true);
  };

  const handleAddResignedEmployees = async () => {
    UpdateEmployeeAsResigned(selectedRowKeys)
      .then((response: any) => {
        enqueueSnackbar("Changes Saved Successfully", {
          variant: "success",
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
        });
        fetchEmailGroups();
      })
      .catch((error: any) => {
        if (error.response.data.errors.EmailLicensing[0])
          enqueueSnackbar(error.response.data.errors.EmailLicensing[0], {
            variant: "error",
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
          });
        else {
          let errorMsg;
          if (axios.isAxiosError(error)) {
            const axiosError = error as AxiosError;
            errorMsg = axiosErrorMessages(axiosError);
          } else {
            errorMsg = error.message;
          }
          enqueueSnackbar(errorMsg, {
            variant: "error",
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
          });
        }
        // enqueueSnackbar("Error in fetching data",{ variant: 'error', anchorOrigin: {
        //   vertical: 'top',
        //   horizontal: 'right',
        // },})
      });
    setSelectedRowKeys([]);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record: any) => ({
      // Disable checkbox for rows where a condition is met
      disabled: !record.isActive || userAppRole !== "Admin", // Replace with your condition based on the row data
    }),
  };

  const edit = (rowData: any) => {
    setIsAdd(false);
    setEditData(rowData);
    setIsAddNewGroup(true);
  };
  const handleClose = () => {
    setIsAddNewGroup(false);
    fetchEmailGroups();
  };
  const handleFilterClose = () => {
    setIsFilter(false);
  };
  const handleFilterData = (filterData: any) => {
    setIsFilter(false);
    setPostData(filterData);
  };

  useEffect(() => {
    handleFilterData(postData);
  }, [postData]);

  const handleChange: TableProps<EmailItem>["onChange"] = (
    pagination,
    filters,
    sorter
  ) => {
    setSortedInfo(sorter as SorterResult<EmailItem>);
    setCurrentPage(pagination.current || 1);
    setPageSize(pagination.pageSize ?? 50);
  };

  const column = [
    {
      title: "Group Name",
      dataIndex: "groupName",
      key: "groupName",

      render: (text: string) => <div>{text}</div>,
    },
    {
      title: "Group Email Id",
      dataIndex: "groupEmailId",
      key: "groupEmailId",

      render: (text: string) => <div>{text}</div>,
    },
  ];

  return (
    <>
      <AdminLayout>
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
        <div>
          <div className="employee-table-header ">
            <Row
              gutter={16}
              style={{ alignItems: "center", margin: "12px 16px 0 16px" }}
            >
              <Col className="gutter-row" lg={12} sm={24}>
                <h3 className="bottom-margin-mob" style={{ float: "left" }}>
                  Email License
                </h3>
              </Col>
              <Col
                className="gutter-row pm-search-flex"
                lg={{ span: 10, offset: 2 }}
                sm={24}
                xs={24}
                style={{ display: "flex" }}
              >
                {userAppRole && (
                  <>
                    <Button
                      onClick={handleAddResignedEmployees}
                      type="primary"
                      danger
                      style={{ marginLeft: 0 }}
                      size="small"
                      disabled={selectedRowKeys.length === 0}
                    >
                      Mark As Resigned
                    </Button>

                    <Button
                      onClick={handleAddNewEmailList}
                      type="primary"
                      style={{ marginLeft: 10 }}
                      size="small"
                    >
                      Add New
                    </Button>
                  </>
                )}

                <Button
                  onClick={handleFilter}
                  type="primary"
                  style={{ marginLeft: 10, marginRight: 10 }}
                  size="small"
                >
                  Filter
                </Button>
                <SearchBar
                  width={"100%"}
                  onSearch={handleSearch}
                  placeholder={"Search"}
                />
              </Col>
            </Row>
          </div>

          <Table
            sortDirections={customSortDirections}
            scroll={{ x: 1000, y: 500 }}
            className="status-list-admin"
            columns={columns}
            rowSelection={rowSelection}
            tableLayout="fixed"
            size="small"
            onChange={handleChange}
            rowClassName="editable-row"
            pagination={{
              current: currentPage,
              showSizeChanger: true,
              defaultPageSize: 50,
            }}
            dataSource={searchText ? filteredData : emailList}
          />

          {isAddNewGroup && (
            <AddEditEmailList
              isAdd={isAdd}
              editData={editData}
              handleClose={handleClose}
            />
          )}
          {isFilter && (
            <FilterEmailList
              filterData={postData}
              handleFilterClose={handleFilterClose}
              handleFilterData={handleFilterData}
            />
          )}
        </div>
      </AdminLayout>

      <Modal
        maskClosable={false}
        title="Group List"
        open={isModalOpen}
        onCancel={handleCancel}
        style={{ marginTop: 60 }}
        footer={null}
        width={600}
      >
        <Table
          size="small"
          dataSource={groups}
          pagination={{
            pageSize: 50,
            current: currentPage,
            showSizeChanger: true,
            // You can also set a default pageSize if needed
            hideOnSinglePage: true,
          }}
          columns={column}
        />
      </Modal>

      <Modal
        maskClosable={false}
        title="Special Email Ids"
        open={isSpecialEmailIdModalOpen}
        onCancel={() => setSpecialEmailIdModal(false)}
        style={{ marginTop: 60 }}
        footer={null}
        width={600}
      >
        <List
          size="small"
          className="demo-loadmore-list"
          itemLayout="horizontal"
          dataSource={specialEmailIds}
          renderItem={(item: any, index) => (
            <List.Item>
              <Skeleton avatar title={false} loading={item.loading} active>
                <List.Item.Meta title={item} />
              </Skeleton>
            </List.Item>
          )}
        />
      </Modal>
    </>
  );
};
export default EmailList;
